import { useState } from 'react';
import { Info, Send } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { EmptyState } from '@/ui/EmptyState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Textarea } from '@/ui/base/textarea';

import { useMensagensDaMedicao } from './dados';

interface ChatComSmeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  medicaoId: string;
  competencia: string;
}

/**
 * Conversa com a SME sobre uma medição específica.
 *
 * NÃO ENVIA. Não há API de mensagens: "Enviar mensagem" mostra o aviso de
 * indisponibilidade, o texto digitado fica no campo e o modal continua
 * aberto — pôr a mensagem na conversa seria fingir que a SME a recebeu. Um
 * aviso fixo acompanha o campo, porque aviso passageiro some e a captura de
 * tela fica parecendo envio. Mesmo padrão de `SlaAprovacaoSmePage` e de
 * `financeiro/solicitacoes-sme/NovaSolicitacaoSmeModal.tsx`; ver `dados.ts`.
 */
export function ChatComSmeModal({
  open,
  onOpenChange,
  medicaoId,
  competencia,
}: ChatComSmeModalProps) {
  const mensagens = useMensagensDaMedicao(open ? medicaoId : null);

  const [texto, setTexto] = useState('');

  const enviar = () => {
    if (!texto.trim()) {
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Mensagens com a SME"
      description={`Sobre a competência ${competencia}`}
      desktopMaxWidth="max-w-lg"
    >
      <div className="space-y-3">
        <div className="flex max-h-[50vh] flex-col gap-3">
          <div className="flex-1 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
            {mensagens.isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Carregando conversa…</p>
            ) : !mensagens.data || mensagens.data.length === 0 ? (
              <EmptyState
                variant="messages"
                title="Nenhuma mensagem ainda"
                description="As mensagens sobre esta medição trocadas com a SME aparecem aqui."
              />
            ) : (
              mensagens.data.map((mensagem) => (
                <div
                  key={mensagem.id}
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    mensagem.autor === 'credenciado'
                      ? 'ml-auto bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p>{mensagem.texto}</p>
                  <p className="mt-1 text-[10px] opacity-70">
                    {new Date(mensagem.enviada_em).toLocaleString('pt-BR')}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="flex items-end gap-2">
            <Textarea
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              placeholder="Escreva uma mensagem…"
              rows={2}
              className="flex-1"
              aria-label="Mensagem para a SME"
            />
            <Button
              size="icon"
              onClick={enviar}
              disabled={!texto.trim()}
              aria-label="Enviar mensagem"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            O envio de mensagens à SME ainda não está disponível. Você pode redigir o texto, mas
            nada é enviado.
          </AlertDescription>
        </Alert>
      </div>
    </ResponsiveModal>
  );
}
